import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { api } from '../../services/api-client';
import { useToast } from '../../contexts/ToastContext';
import { useConfirmationDialog } from '../../utils/ConfirmationDialogContext';
import { ExcelCellData, ExcelRow, ExcelViewCycle, ExcelViewResponse } from '../../types/excelView';

interface UseExcelViewProps {
  cycleId: number | null;
}

/** Targeting for the auto-progression bulk action. */
export type ProgressionTarget =
  | { kind: 'all' }
  | { kind: 'exercise'; exerciseId: number }
  | { kind: 'day'; dayNumbers: number[] };

export interface AutoProgressOptions {
  stepKg: number;
  baseWeekIndex: number;
  target: ProgressionTarget;
}

/** Update payload for the `updateExercises` batch endpoint (one item per cell). */
interface ExerciseInstanceUpdate {
  id: number;
  repetitions?: string;
  weight?: string;
  sets?: string;
  tempo?: string;
  restInterval?: string;
  rpe?: string;
  duration?: string;
  distance?: string;
  difficulty?: string;
  notes?: string;
}

export function useExcelView({ cycleId }: UseExcelViewProps) {
  const intl = useIntl();
  const { showToast } = useToast();
  const { showConfirmationDialog } = useConfirmationDialog();

  const [data, setData] = useState<ExcelViewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [savingCells, setSavingCells] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    if (!cycleId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.workout.fetchExcelView(cycleId);
      // Response is wrapped in `{ data, message, status }` by the interceptor.
      const body = (res?.data ?? res) as ExcelViewResponse;
      setData(body);
    } catch (e) {
      setError((e as Error).message || 'load_failed');
      showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [cycleId, refreshTick, intl, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // ── derived: rows pivoted by (dayNumber, exerciseId) ──────────────────
  const rows = useMemo<ExcelRow[]>(() => {
    if (!data?.cycle) return [];
    return buildRows(data.cycle);
  }, [data]);

  const weekNumbers = useMemo<number[]>(() => {
    if (!data?.cycle) return [];
    return data.cycle.weeks.map((w) => w.weekNumber).sort((a, b) => a - b);
  }, [data]);

  // ── PR / deload helpers ──────────────────────────────────────────────
  const isPrForRow = useCallback(
    (row: ExcelRow, cell: ExcelCellData | null): boolean => {
      if (!cell || !data?.prByExercise) return false;
      const pr = data.prByExercise[row.exerciseId];
      if (!pr) return true; // No history → planned cell is a "first time".
      const planWeight = parseFloat(cell.weight ?? '');
      const planReps = parseFloat(cell.repetitions ?? '');
      const prWeight = parseFloat(pr.weight);
      const prReps = parseFloat(pr.repetitions);
      if (!Number.isFinite(planWeight) || !Number.isFinite(planReps)) return false;
      if (!Number.isFinite(prWeight) || !Number.isFinite(prReps)) return true;
      return planReps * planWeight > prReps * prWeight;
    },
    [data]
  );

  const isDeload = useCallback((row: ExcelRow, cellIndex: number): boolean => {
    if (cellIndex === 0) return false;
    const cell = row.cells[cellIndex];
    const prevCell = row.cells[cellIndex - 1];
    if (!cell || !prevCell) return false;
    const w = parseFloat(cell.weight ?? '');
    const pw = parseFloat(prevCell.weight ?? '');
    if (!Number.isFinite(w) || !Number.isFinite(pw)) return false;
    return w < pw;
  }, []);

  // ── mutations ───────────────────────────────────────────────────────
  const submitUpdates = useCallback(
    async (updates: ExerciseInstanceUpdate[]) => {
      if (updates.length === 0) return;
      // Mark all updating cells as in flight for the pulsing border.
      setSavingCells((prev) => {
        const next = new Set(prev);
        updates.forEach((u) => next.add(u.id));
        return next;
      });
      try {
        // Reuse the existing batch endpoint.
        // The wrapper expects `UpdateExerciseInstanceDto[]`.
        await api.workout.updateExercisesInstance(updates);
      } catch (e) {
        showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
        throw e;
      } finally {
        setSavingCells((prev) => {
          const next = new Set(prev);
          updates.forEach((u) => next.delete(u.id));
          return next;
        });
      }
    },
    [showToast, intl]
  );

  const updateCell = useCallback(
    async (exerciseInstanceId: number, patch: Partial<ExerciseInstanceUpdate>) => {
      // Optimistic local update so the UI doesn't snap back while saving.
      setData((prev) => prev && patchCellInCycle(prev, exerciseInstanceId, patch));
      try {
        await submitUpdates([{ id: exerciseInstanceId, ...patch }]);
      } catch {
        // Rollback by refetching authoritative data.
        setRefreshTick((n) => n + 1);
      }
    },
    [submitUpdates]
  );

  // ── bulk actions ────────────────────────────────────────────────────
  /**
   * Apply +stepKg per week starting from `baseWeekIndex`. Weeks before the
   * base are untouched; from the base forward, each week gets base + N*step.
   *
   * `target` filters which rows are affected:
   *   - { kind: 'all' } → every row
   *   - { kind: 'exercise', exerciseId } → only rows with that exerciseId
   *   - { kind: 'day', dayNumbers } → only rows whose dayNumber is included
   */
  const autoProgress = useCallback(
    async (opts: AutoProgressOptions) => {
      if (!data) return;
      const { stepKg, baseWeekIndex, target } = opts;

      const matchesTarget = (row: ExcelRow): boolean => {
        if (target.kind === 'all') return true;
        if (target.kind === 'exercise') return row.exerciseId === target.exerciseId;
        if (target.kind === 'day') return target.dayNumbers.includes(row.dayNumber);
        return false;
      };

      const updates: ExerciseInstanceUpdate[] = [];
      for (const row of rows) {
        if (!matchesTarget(row)) continue;
        const baseCell = row.cells[baseWeekIndex];
        const baseWeight = parseFloat(baseCell?.weight ?? '');
        if (!Number.isFinite(baseWeight)) continue;
        for (let i = baseWeekIndex + 1; i < row.cells.length; i++) {
          const cell = row.cells[i];
          if (!cell) continue;
          const nextWeight = (baseWeight + (i - baseWeekIndex) * stepKg).toFixed(1);
          if (nextWeight === cell.weight) continue;
          updates.push({ id: cell.exerciseInstanceId, weight: nextWeight });
        }
      }
      if (updates.length === 0) {
        showToast(
          'info',
          intl.formatMessage({ id: 'common.info', defaultMessage: 'Info' }),
          intl.formatMessage({
            id: 'excelView.bulk.noChanges',
            defaultMessage: 'No hay cambios para aplicar.'
          })
        );
        return;
      }
      await submitUpdates(updates);
      setRefreshTick((n) => n + 1);
      showToast(
        'success',
        intl.formatMessage({ id: 'common.success', defaultMessage: 'Listo' }),
        intl.formatMessage(
          { id: 'excelView.bulk.applied', defaultMessage: '{n} celdas actualizadas.' },
          { n: updates.length }
        )
      );
    },
    [data, rows, intl, submitUpdates, showToast]
  );

  const copyColumn = useCallback(
    (sourceWeekIndex: number) => {
      if (!data) return;
      const targetIdx = sourceWeekIndex + 1;
      if (targetIdx >= weekNumbers.length) return;
      showConfirmationDialog({
        message: intl.formatMessage(
          {
            id: 'excelView.bulk.copyColumn.confirm',
            defaultMessage: '¿Copiar los valores de Semana {src} a Semana {dst}?'
          },
          { src: weekNumbers[sourceWeekIndex], dst: weekNumbers[targetIdx] }
        ),
        header: intl.formatMessage({ id: 'common.confirmation', defaultMessage: 'Confirmar' }),
        icon: 'pi pi-copy',
        accept: async () => {
          const updates: ExerciseInstanceUpdate[] = [];
          for (const row of rows) {
            const src = row.cells[sourceWeekIndex];
            const dst = row.cells[targetIdx];
            if (!src || !dst) continue;
            updates.push({
              id: dst.exerciseInstanceId,
              repetitions: src.repetitions ?? undefined,
              weight: src.weight ?? undefined,
              sets: src.sets ?? undefined
            });
          }
          await submitUpdates(updates);
          setRefreshTick((n) => n + 1);
        }
      });
    },
    [data, rows, weekNumbers, intl, showConfirmationDialog, submitUpdates]
  );

  const duplicateWeek = useCallback(
    (sourceWeekIndex: number) => {
      if (!cycleId) return;
      showConfirmationDialog({
        message: intl.formatMessage(
          {
            id: 'excelView.bulk.duplicateWeek.confirm',
            defaultMessage: '¿Duplicar Semana {n}? Se va a agregar una nueva semana inmediatamente después.'
          },
          { n: weekNumbers[sourceWeekIndex] ?? sourceWeekIndex + 1 }
        ),
        header: intl.formatMessage({ id: 'common.confirmation', defaultMessage: 'Confirmar' }),
        icon: 'pi pi-clone',
        accept: async () => {
          try {
            setLoading(true);
            await api.workout.duplicateCycleWeek(cycleId, sourceWeekIndex);
            showToast(
              'success',
              intl.formatMessage({ id: 'common.success', defaultMessage: 'Listo' }),
              intl.formatMessage({
                id: 'excelView.bulk.duplicateWeek.success',
                defaultMessage: 'Semana duplicada.'
              })
            );
            setRefreshTick((n) => n + 1);
          } catch (e) {
            showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
          } finally {
            setLoading(false);
          }
        }
      });
    },
    [cycleId, weekNumbers, intl, showConfirmationDialog, showToast]
  );

  const refresh = () => setRefreshTick((n) => n + 1);

  // ── cross-week structural edits (delegate to backend) ─────────────────
  const addGroup = useCallback(
    async (dayNumber: number, name?: string): Promise<boolean> => {
      if (!cycleId) return false;
      try {
        await api.workout.addCycleGroup(cycleId, { dayNumber, name });
        setRefreshTick((n) => n + 1);
        return true;
      } catch (e) {
        showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
        return false;
      }
    },
    [cycleId, intl, showToast]
  );

  const addExercise = useCallback(
    async (params: {
      dayNumber: number;
      groupNumber: number;
      exerciseId: number;
      sets?: string;
      repetitions?: string;
      weight?: string;
    }): Promise<boolean> => {
      if (!cycleId) return false;
      try {
        await api.workout.addCycleExercise(cycleId, params);
        setRefreshTick((n) => n + 1);
        return true;
      } catch (e) {
        showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
        return false;
      }
    },
    [cycleId, intl, showToast]
  );

  const moveExercise = useCallback(
    async (params: {
      dayNumber: number;
      fromGroupNumber: number;
      fromRowIndex: number;
      toGroupNumber: number;
      toRowIndex: number;
    }) => {
      if (!cycleId) return;
      try {
        await api.workout.moveCycleExercise(cycleId, params);
        setRefreshTick((n) => n + 1);
      } catch (e) {
        showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
      }
    },
    [cycleId, intl, showToast]
  );

  const moveGroup = useCallback(
    async (params: { dayNumber: number; fromGroupNumber: number; toGroupNumber: number }) => {
      if (!cycleId) return;
      try {
        await api.workout.moveCycleGroup(cycleId, params);
        setRefreshTick((n) => n + 1);
      } catch (e) {
        showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
      }
    },
    [cycleId, intl, showToast]
  );

  const deleteExercise = useCallback(
    (dayNumber: number, exerciseId: number, exerciseName: string) => {
      if (!cycleId) return;
      showConfirmationDialog({
        message: intl.formatMessage(
          {
            id: 'excelView.deleteExercise.confirm',
            defaultMessage: '¿Eliminar "{name}" de todas las semanas del ciclo? Los logs de sets quedan intactos.'
          },
          { name: exerciseName }
        ),
        header: intl.formatMessage({ id: 'common.confirmation', defaultMessage: 'Confirmar' }),
        icon: 'pi pi-trash',
        accept: async () => {
          try {
            await api.workout.deleteCycleExercise(cycleId, dayNumber, exerciseId);
            setRefreshTick((n) => n + 1);
          } catch (e) {
            showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
          }
        }
      });
    },
    [cycleId, intl, showToast, showConfirmationDialog]
  );

  const deleteGroup = useCallback(
    (dayNumber: number, groupNumber: number, groupName?: string | null) => {
      if (!cycleId) return;
      showConfirmationDialog({
        message: intl.formatMessage(
          {
            id: 'excelView.deleteGroup.confirm',
            defaultMessage: '¿Eliminar el grupo "{name}" y todos sus ejercicios de todas las semanas?'
          },
          { name: groupName || `Grupo ${groupNumber}` }
        ),
        header: intl.formatMessage({ id: 'common.confirmation', defaultMessage: 'Confirmar' }),
        icon: 'pi pi-trash',
        accept: async () => {
          try {
            await api.workout.deleteCycleGroup(cycleId, dayNumber, groupNumber);
            setRefreshTick((n) => n + 1);
          } catch (e) {
            showToast('error', intl.formatMessage({ id: 'error', defaultMessage: 'Error' }), (e as Error).message);
          }
        }
      });
    },
    [cycleId, intl, showToast, showConfirmationDialog]
  );

  return {
    intl,
    data,
    rows,
    weekNumbers,
    loading,
    error,
    savingCells,
    isPrForRow,
    isDeload,
    updateCell,
    autoProgress,
    copyColumn,
    duplicateWeek,
    addGroup,
    addExercise,
    moveExercise,
    moveGroup,
    deleteExercise,
    deleteGroup,
    refresh
  };
}

// ───────────────────────────────────────────────────────────────────────
// Pure helpers (exported for unit tests)
// ───────────────────────────────────────────────────────────────────────

/**
 * Pivot the nested cycle entity tree into `rows-by-(day, exercise) × cells-by-week`.
 *
 * Each cell is positioned by `weekNumber - 1` (1-based weekNumber → 0-based array).
 * Cells without a session for that (week, day, exercise) stay `null`.
 */
export function buildRows(cycle: ExcelViewCycle): ExcelRow[] {
  // Key includes groupNumber so the SAME exercise used in two groups on the
  // same day stays in distinct rows. Before this we keyed only by exerciseId
  // and the cells silently overwrote each other.
  const byKey = new Map<string, ExcelRow>();
  // Track every (dayNumber, groupNumber, groupName) tuple seen so we can
  // surface empty groups too (added but no exercises yet).
  const groupsByDay = new Map<number, Map<number, string | null>>();
  const totalWeeks = cycle.weeks.length;

  for (const week of cycle.weeks) {
    const weekIdx = week.weekNumber - 1;
    for (const session of week.trainingSessions ?? []) {
      const day = session.dayNumber;
      if (day == null) continue;
      const dayGroups = groupsByDay.get(day) ?? new Map<number, string | null>();
      groupsByDay.set(day, dayGroups);

      for (const wi of session.workoutInstances ?? []) {
        for (const group of wi.groups ?? []) {
          if (!dayGroups.has(group.groupNumber)) {
            dayGroups.set(group.groupNumber, group.name ?? null);
          }
          for (const ex of group.exercises ?? []) {
            const exId = ex.exercise?.id;
            if (!exId) continue;
            const key = `${day}-${group.groupNumber}-${exId}`;
            if (!byKey.has(key)) {
              byKey.set(key, {
                key,
                dayNumber: day,
                groupNumber: group.groupNumber,
                groupName: group.name,
                exerciseId: exId,
                exerciseName: ex.exercise.name,
                cells: new Array(totalWeeks).fill(null)
              });
            }
            const row = byKey.get(key)!;
            if (weekIdx >= 0 && weekIdx < totalWeeks) {
              row.cells[weekIdx] = {
                exerciseInstanceId: ex.id,
                weekNumber: week.weekNumber,
                sets: ex.sets,
                repetitions: ex.repetitions,
                weight: ex.weight,
                setConfiguration: ex.setConfiguration
              };
            }
          }
        }
      }
    }
  }

  // Add synthetic empty-group rows for any (day, group) seen but with no
  // exercises. These render as a group-header only (no exercise tr) so the
  // coach knows the group exists and can target it from AddExerciseDialog.
  groupsByDay.forEach((groupMap, day) => {
    groupMap.forEach((name, groupNumber) => {
      const hasAny = Array.from(byKey.values()).some((r) => r.dayNumber === day && r.groupNumber === groupNumber);
      if (!hasAny) {
        const key = `${day}-${groupNumber}-empty`;
        byKey.set(key, {
          key,
          dayNumber: day,
          groupNumber,
          groupName: name,
          exerciseId: -groupNumber, // synthetic — never matches a real exercise
          exerciseName: '',
          cells: new Array(totalWeeks).fill(null)
        });
      }
    });
  });

  return [...byKey.values()].sort((a, b) => {
    if (a.dayNumber !== b.dayNumber) return a.dayNumber - b.dayNumber;
    if (a.groupNumber !== b.groupNumber) return a.groupNumber - b.groupNumber;
    return a.exerciseName.localeCompare(b.exerciseName);
  });
}

/** Locate the active week index (today between startDate and endDate). */
export function findActiveWeekIndex(cycle: ExcelViewCycle, now: Date = new Date()): number {
  const t = now.getTime();
  for (let i = 0; i < cycle.weeks.length; i++) {
    const w = cycle.weeks[i];
    if (!w || !w.startDate || !w.endDate) continue;
    const start = new Date(w.startDate).getTime();
    const end = new Date(w.endDate).getTime();
    if (t >= start && t <= end) return i;
  }
  return -1;
}

/**
 * Immutable patch: returns a new ExcelViewResponse with the matching cell
 * updated. Used for optimistic local updates.
 */
function patchCellInCycle(
  data: ExcelViewResponse,
  exerciseInstanceId: number,
  patch: Partial<{
    repetitions: string;
    weight: string;
    sets: string;
    tempo: string;
    restInterval: string;
  }>
): ExcelViewResponse {
  // Strip undefined keys — spreading `{ foo: undefined }` over the entity
  // would CLEAR the existing value, which is what caused the popover to
  // erase the unchanged field on save until refetch landed.
  const cleanPatch: Record<string, string> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v !== undefined) cleanPatch[k] = v as string;
  }
  if (Object.keys(cleanPatch).length === 0) return data;

  const cycle = data.cycle;
  const newCycle = {
    ...cycle,
    weeks: cycle.weeks.map((week) => ({
      ...week,
      trainingSessions: week.trainingSessions.map((s) => ({
        ...s,
        workoutInstances: s.workoutInstances.map((wi) => ({
          ...wi,
          groups: wi.groups.map((g) => ({
            ...g,
            exercises: g.exercises.map((e) => (e.id === exerciseInstanceId ? { ...e, ...cleanPatch } : e))
          }))
        }))
      }))
    }))
  };
  return { ...data, cycle: newCycle };
}

// re-export for tests
export type { ExerciseInstanceUpdate };
